import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { recognitionAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole, getErrorMessage } from '@/lib/utils';
import {
  Loader2, ArrowLeft, MapPin, CalendarDays, Clock, FileText, CheckCircle2,
  XCircle, DoorOpen, ShieldCheck, Ship, Users, Gem, Hourglass,
  Home, Target, HelpCircle, Megaphone, Eye, Bell, Star,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#F4B91E';

const DEFAULT_HERO = 'https://images.unsplash.com/photo-1554254648-2d58a1bc3fd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Home },
  { id: 'qualify', label: 'How to Qualify', icon: Target },
  { id: 'dates', label: 'Important Dates', icon: CalendarDays },
  { id: 'rules', label: 'Official Rules', icon: FileText },
  { id: 'faq', label: 'FAQ', icon: HelpCircle },
  { id: 'announcements', label: 'Updates', icon: Megaphone },
];

function useCountdown(eventAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!eventAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [eventAt]);
  if (!eventAt) return null;
  const diff = new Date(eventAt).getTime() - now;
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
  };
}

const ComingSoon = ({ title, sub }) => (
  <div className="text-center py-14 border border-white/10 rounded-2xl bg-white/[0.02]" data-testid="recognition-coming-soon">
    <Star size={28} className="mx-auto mb-3" style={{ color: GOLD }} />
    <p className="text-lg font-semibold tracking-wide">{title}</p>
    {sub && <p className="text-sm text-white/50 mt-1 max-w-md mx-auto">{sub}</p>}
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-xs font-semibold uppercase tracking-[0.3em] mb-3" style={{ color: GOLD }}>{children}</h2>
);

const Prose = ({ children }) => (
  <div className="text-[15px] leading-relaxed text-white/80 whitespace-pre-wrap">{children}</div>
);

function Highlight({ icon: Icon, title, sub }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={28} style={{ color: GOLD }} className="shrink-0" />
      <div>
        <p className="text-sm font-bold uppercase tracking-wide leading-tight">{title}</p>
        {sub && <p className="text-xs text-white/60">{sub}</p>}
      </div>
    </div>
  );
}

function InvitationCard({ program, onDone }) {
  const questions = program.prequal_questions || [];
  const [answers, setAnswers] = useState({});
  const [attend, setAttend] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (attend === null) { toast.error('Please indicate whether you can attend'); return; }
    if (attend && questions.some((q) => answers[q.id] === undefined)) { toast.error('Please answer every question'); return; }
    setSubmitting(true);
    try {
      await recognitionAPI.respond(program.program_id, { attend, prequal_answers: questions.map((q) => ({ question_id: q.id, answer: !!answers[q.id] })) });
      toast.success('Your response has been submitted'); onDone();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setSubmitting(false); }
  };
  return (
    <div className="rounded-2xl border p-6 mb-8" style={{ borderColor: `${GOLD}66`, background: 'rgba(244,185,30,0.06)' }} data-testid="recognition-invitation-card">
      <div className="flex items-center gap-2 mb-1"><Gem size={20} style={{ color: GOLD }} /><h2 className="text-xl font-bold">You've been selected</h2></div>
      <p className="text-sm text-white/70 mb-5">Complete your pre-qualification and confirm attendance. KR will review and approve participants.</p>
      {questions.length > 0 && (
        <div className="space-y-3 mb-5">
          {questions.map((q) => (
            <div key={q.id} className="flex items-center justify-between gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-3" data-testid={`prequal-q-${q.id}`}>
              <span className="text-sm text-white/85">{q.text}</span>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setAnswers((a) => ({ ...a, [q.id]: true }))} className={`px-3 py-1 rounded-full text-xs font-semibold border ${answers[q.id] === true ? 'text-black' : 'text-white/60 border-white/20'}`} style={answers[q.id] === true ? { background: GOLD, borderColor: GOLD } : {}} data-testid={`prequal-${q.id}-yes`}>Yes</button>
                <button onClick={() => setAnswers((a) => ({ ...a, [q.id]: false }))} className={`px-3 py-1 rounded-full text-xs font-semibold border ${answers[q.id] === false ? 'bg-red-500 text-white border-red-500' : 'text-white/60 border-white/20'}`} data-testid={`prequal-${q.id}-no`}>No</button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-sm font-semibold mb-2">Can you attend?</p>
      <div className="flex gap-3 mb-5">
        <button onClick={() => setAttend(true)} className={`flex-1 flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold ${attend === true ? 'text-black' : 'text-white/70 border-white/20'}`} style={attend === true ? { background: GOLD, borderColor: GOLD } : {}} data-testid="attend-yes"><CheckCircle2 size={16} /> Yes, I can attend</button>
        <button onClick={() => setAttend(false)} className={`flex-1 flex items-center justify-center gap-2 rounded-lg border p-3 text-sm font-semibold ${attend === false ? 'bg-red-500 text-white border-red-500' : 'text-white/70 border-white/20'}`} data-testid="attend-no"><XCircle size={16} /> No, I can't attend</button>
      </div>
      <button onClick={submit} disabled={submitting} className="w-full rounded-lg py-3 text-sm font-bold text-black flex items-center justify-center gap-2" style={{ background: GOLD }} data-testid="recognition-respond-submit">
        {submitting ? <Loader2 size={15} className="animate-spin" /> : null} Submit response
      </button>
    </div>
  );
}

function PrivateArea({ programId, preview }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  useEffect(() => {
    recognitionAPI.getPrivate(programId).then((r) => setData(r.data)).catch((e) => toast.error(getErrorMessage(e))).finally(() => setLoading(false));
  }, [programId]);
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin" size={24} /></div>;
  if (!data) return null;
  const pc = data.private_content || {};
  const photos = (data.private_media || []).filter((m) => m.mime?.startsWith('image/'));
  const docs = (data.private_media || []).filter((m) => !m.mime?.startsWith('image/'));
  const Block = ({ title, body, icon: Icon }) => body ? (
    <div className="mb-7"><h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] mb-2" style={{ color: GOLD }}>{Icon && <Icon size={14} />}{title}</h3><Prose>{body}</Prose></div>
  ) : null;
  return (
    <div data-testid="recognition-private-area">
      <div className="rounded-xl border p-4 mb-7 flex items-center gap-3" style={{ borderColor: `${GOLD}55`, background: 'rgba(244,185,30,0.07)' }}>
        <DoorOpen size={22} style={{ color: GOLD }} />
        <div><p className="font-bold">{preview ? 'Admin preview — attendee area' : 'Welcome, approved participant'}</p>
          <p className="text-sm text-white/70">{preview ? 'This is exactly what an approved agent sees after tapping Enter.' : 'Here are the full details for your experience.'}</p></div>
      </div>
      <Block title="Trip Details" body={pc.trip_details} icon={MapPin} />
      <Block title="Itinerary" body={pc.itinerary} icon={CalendarDays} />
      <Block title="Schedule" body={pc.schedule} icon={Clock} />
      <Block title="Requirements" body={pc.requirements} icon={ShieldCheck} />
      {photos.length > 0 && (
        <div className="mb-7">
          <h3 className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>Destination Photos</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {photos.map((m) => (
              <div key={m.media_id} className="rounded-xl overflow-hidden border border-white/10">
                <img src={`${BACKEND}${m.url}`} alt={m.caption || 'Photo'} className="w-full h-44 object-cover" />
                {m.caption && <p className="text-xs text-white/50 p-2">{m.caption}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {docs.length > 0 && (
        <div><h3 className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>Documents</h3>
          <div className="space-y-2">{docs.map((m) => (
            <a key={m.media_id} href={`${BACKEND}${m.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline" style={{ color: GOLD }}><FileText size={14} /> {m.name}</a>
          ))}</div></div>
      )}
      {!pc.trip_details && !pc.itinerary && !pc.schedule && !pc.requirements && photos.length === 0 && docs.length === 0 && (
        <ComingSoon title="Itinerary coming soon" sub="Full trip details will appear here as they're finalized." />
      )}
    </div>
  );
}

export default function RecognitionProgramPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const [sp] = useSearchParams();
  const previewPrivate = isKR && sp.get('preview') === 'private';
  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState(null);
  const [tab, setTab] = useState('overview');
  const [entered, setEntered] = useState(false);

  const load = useCallback(() => {
    recognitionAPI.getProgram(id).then((r) => setProgram(r.data))
      .catch((e) => { toast.error(getErrorMessage(e)); navigate('/recognition'); })
      .finally(() => setLoading(false));
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const cd = useCountdown(program?.event_at);

  if (loading) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin" size={30} /></div>;
  if (!program) return null;

  const part = program.my_participation;
  const hasCustomCover = Boolean(program.cover_image_url);
  const heroImg = hasCustomCover ? `${BACKEND}${program.cover_image_url}` : DEFAULT_HERO;
  const uploaded = (program.public_media || []).filter((m) => m.mime?.startsWith('image/')).map((m) => `${BACKEND}${m.url}`);
  const gallery = uploaded.slice(0, 4);
  const statusLabel = { coming_soon: 'Coming Soon', qualification_open: 'Qualification Open', selection: 'Selection in Progress', confirmed: 'Participants Confirmed', completed: 'Completed', archived: 'Archived' }[program.status] || 'Coming Soon';

  const keyDetails = [
    { icon: MapPin, label: 'Destination', value: program.destination },
    { icon: CalendarDays, label: 'Expected Dates', value: program.event_dates },
    { icon: Clock, label: 'Qualification Period', value: program.qualification_period },
    { icon: Users, label: 'Available Spots', value: program.spots || '5 Agents (each with one guest)' },
  ].filter((d) => d.value);

  return (
    <div className="bg-[#001536] text-white min-h-[calc(100vh-4rem)]" data-testid="recognition-program-page" style={{ backgroundImage: 'radial-gradient(900px 500px at 80% -10%, rgba(26,78,136,0.35), transparent)' }}>
      <div className="px-6 pt-5">
        <button onClick={() => navigate('/recognition')} className="flex items-center gap-1 text-sm text-white/50 hover:text-white transition-colors" data-testid="recognition-back"><ArrowLeft size={16} /> All programs</button>
      </div>

      {/* Cinematic hero */}
      {hasCustomCover ? (
        <section className="mx-4 md:mx-6 mt-4 rounded-3xl overflow-hidden" data-testid="recognition-hero-cover">
          <img src={heroImg} alt={program.name} className="w-full h-auto block" />
        </section>
      ) : (
        <section className="relative mx-4 md:mx-6 mt-4 rounded-3xl overflow-hidden min-h-[420px] md:min-h-[460px] flex items-end">
          <img src={heroImg} alt={program.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,13,38,0.92) 0%, rgba(0,21,54,0.65) 45%, rgba(0,21,54,0.15) 100%)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(0,13,38,0.9) 0%, transparent 55%)' }} />
          <div className="relative z-10 p-7 md:p-12 max-w-3xl">
            <p className="text-[11px] md:text-xs tracking-[0.45em] text-white/80 font-medium">KR ENTERPRISE GROUP</p>
            <p className="text-[10px] md:text-[11px] tracking-[0.5em] text-white/50 mb-3">PRESENTS</p>
            <h1 className="leading-none" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="block text-4xl md:text-6xl font-bold tracking-wide">
                {(() => { const w = (program.name || '').split(' '); return <>{w.slice(0, -1).join(' ')} <span style={{ color: GOLD }}>{w.slice(-1)}</span></>; })()}
              </span>
            </h1>
            {program.edition && <p className="text-xs md:text-sm tracking-[0.4em] text-white/70 mt-3">{program.edition.toUpperCase()}</p>}
            {program.tagline && <p className="text-xl md:text-2xl italic mt-2" style={{ color: GOLD, fontFamily: 'Georgia, serif' }}>{program.tagline}</p>}
            <div className="flex flex-wrap gap-6 md:gap-10 mt-7">
              {program.destination && <Highlight icon={Ship} title="Cruise Experience" sub={`From ${program.destination.replace(/^cruise\s*from\s*/i, '')}`} />}
              <Highlight icon={Users} title="5 Qualifying Agents" sub="Each with one guest" />
              <Highlight icon={Gem} title="All Included" sub="Subject to final program terms" />
            </div>
          </div>
          <div className="absolute top-5 right-5 z-10 text-right">
            <div className="inline-flex flex-col items-center border px-5 py-3 rounded-xl" style={{ borderColor: `${GOLD}66`, background: 'rgba(0,13,38,0.5)' }}>
              <span className="text-[9px] tracking-[0.3em] text-white/60">STATUS</span>
              <span className="text-sm font-bold" style={{ color: GOLD }}>{statusLabel.toUpperCase()}</span>
            </div>
          </div>
        </section>
      )}

      {/* Countdown / coming soon band */}
      <section className="mx-4 md:mx-6 mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-5 flex flex-col md:flex-row items-center gap-6 justify-between">
        {cd ? (
          <>
            <div className="flex items-center gap-5">
              <Hourglass size={30} style={{ color: GOLD }} />
              <div className="flex items-center gap-4" data-testid="recognition-countdown">
                {[['DAYS', cd.days], ['HOURS', cd.hours], ['MINUTES', cd.minutes], ['SECONDS', cd.seconds]].map(([l, v], i) => (
                  <React.Fragment key={l}>
                    <div className="text-center min-w-[52px]"><div className="text-3xl md:text-4xl font-bold tabular-nums" style={{ color: GOLD }}>{String(v).padStart(2, '0')}</div><div className="text-[9px] tracking-[0.25em] text-white/50">{l}</div></div>
                    {i < 3 && <span className="text-2xl text-white/20">:</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <p className="italic text-white/70 text-center md:text-right" style={{ fontFamily: 'Georgia, serif' }}>“Extraordinary agents deserve<br className="hidden md:block" /> extraordinary experiences.”</p>
          </>
        ) : (
          <div className="w-full text-center py-1" data-testid="recognition-coming-band">
            <p className="text-lg md:text-xl font-bold tracking-[0.2em]" style={{ color: GOLD }}>THE EXPERIENCE IS COMING</p>
            <p className="text-sm text-white/60 mt-1">An extraordinary recognition experience awaits.</p>
          </div>
        )}
        <button onClick={() => toast.success("You'll be notified as details are confirmed.")} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-black shrink-0" style={{ background: GOLD }} data-testid="recognition-notify-btn"><Bell size={15} /> Notify Me</button>
      </section>

      {/* Tabs */}
      {!previewPrivate && (
        <nav className="mx-4 md:mx-6 mt-6 border-b border-white/10 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'font-semibold' : 'text-white/55 border-transparent hover:text-white/80'}`} style={tab === t.id ? { color: GOLD, borderColor: GOLD } : {}} data-testid={`recognition-tab-${t.id}`}>
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </nav>
      )}

      {/* Content */}
      <div className="px-4 md:px-6 py-7 pb-16">
        {previewPrivate ? (
          <div className="max-w-4xl">
            <div className="rounded-lg border border-white/15 bg-white/[0.04] px-4 py-2 mb-5 flex items-center gap-2 text-sm text-white/70"><Eye size={15} style={{ color: GOLD }} /> Preview mode — close this tab to return to editing.</div>
            <PrivateArea programId={program.program_id} preview />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 min-w-0">
              {part?.status === 'invited' && <InvitationCard program={program} onDone={load} />}
              {part && ['confirmed', 'pending_review', 'declined', 'rejected'].includes(part.status) && (
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 mb-8 flex items-start gap-3" data-testid="recognition-status-banner">
                  {part.status === 'declined' || part.status === 'rejected' ? <XCircle size={20} className="text-white/50 mt-0.5" /> : <Clock size={20} style={{ color: GOLD }} className="mt-0.5" />}
                  <div><p className="font-semibold">{{ confirmed: 'Response received', pending_review: 'Under review', declined: 'You declined', rejected: 'Not selected' }[part.status]}</p>
                    <p className="text-sm text-white/60">{{ confirmed: 'KR is reviewing participants and will notify you once approved.', pending_review: 'KR will review your case before deciding, based on your pre-qualification answers.', declined: 'You indicated you can’t attend. Thank you for letting us know.', rejected: 'Thank you for your interest. You were not selected at this time.' }[part.status]}</p></div>
                </div>
              )}
              {part?.status === 'approved' && !entered && (
                <div className="rounded-xl border p-5 mb-8 flex items-center justify-between gap-4" style={{ borderColor: `${GOLD}66`, background: 'rgba(244,185,30,0.07)' }} data-testid="recognition-entrar">
                  <div className="flex items-center gap-3"><CheckCircle2 size={24} style={{ color: GOLD }} /><div><p className="font-bold">You're approved</p><p className="text-sm text-white/70">Enter to view your itinerary, schedule and trip details.</p></div></div>
                  <button onClick={() => setEntered(true)} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-black shrink-0" style={{ background: GOLD }} data-testid="recognition-enter-btn"><DoorOpen size={16} /> Enter</button>
                </div>
              )}

              {part?.status === 'approved' && entered ? (
                <PrivateArea programId={program.program_id} />
              ) : (
                <>
                  {tab === 'overview' && (
                    <>
                      <SectionTitle>An Experience Beyond the Ordinary</SectionTitle>
                      <h2 className="text-2xl md:text-3xl font-bold mb-4" style={{ fontFamily: 'Georgia, serif' }}>About {program.name}</h2>
                      {program.description ? <Prose>{program.description}</Prose> : <p className="text-white/50 text-sm">Details coming soon.</p>}
                      {gallery.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 mt-6">
                          {gallery.map((src, i) => (
                            <div key={i} className={`rounded-2xl overflow-hidden border border-white/10 ${i === 0 ? 'col-span-2' : ''}`}>
                              <img src={src} alt="Experience" className={`w-full object-cover ${i === 0 ? 'h-56 md:h-72' : 'h-36 md:h-44'}`} loading="lazy" />
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  {tab === 'qualify' && (program.qualification_details ? (<><SectionTitle>How to Qualify</SectionTitle><Prose>{program.qualification_details}</Prose></>) : <ComingSoon title="Official Qualification Requirements — Coming Soon" sub="Final eligibility and selection criteria will be published following program approval." />)}
                  {tab === 'dates' && (
                    <><SectionTitle>Important Dates & Details</SectionTitle>
                      <div className="divide-y divide-white/10 border border-white/10 rounded-2xl overflow-hidden">
                        {keyDetails.map((d) => (
                          <div key={d.label} className="flex items-center gap-4 px-5 py-4"><d.icon size={18} style={{ color: GOLD }} /><div><p className="text-[11px] uppercase tracking-widest text-white/45">{d.label}</p><p className="text-sm font-medium">{d.value}</p></div></div>
                        ))}
                      </div></>
                  )}
                  {tab === 'rules' && (program.rules ? (<><SectionTitle>Official Rules</SectionTitle><Prose>{program.rules}</Prose>{program.restrictions && <div className="mt-6"><SectionTitle>Restrictions</SectionTitle><Prose>{program.restrictions}</Prose></div>}</>) : <ComingSoon title="Official Rules — Coming Soon" sub="The official program rules will be published after approval." />)}
                  {tab === 'faq' && (program.terms ? (<><SectionTitle>Terms & FAQ</SectionTitle><Prose>{program.terms}</Prose></>) : <ComingSoon title="FAQ — Coming Soon" sub="Frequently asked questions will be added as the program nears." />)}
                  {tab === 'announcements' && <ComingSoon title="No announcements yet" sub="Official updates about THE ESCAPE will appear here." />}
                </>
              )}
            </div>

            <aside className="space-y-4">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <h3 className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: GOLD }}>Key Details</h3>
                <dl className="space-y-4">
                  {keyDetails.map((d) => (
                    <div key={d.label} className="flex items-start gap-3"><d.icon size={16} style={{ color: GOLD }} className="mt-0.5" /><div><dt className="text-[10px] uppercase tracking-widest text-white/45">{d.label}</dt><dd className="text-sm">{d.value}</dd></div></div>
                  ))}
                  <div className="flex items-start gap-3"><Star size={16} style={{ color: GOLD }} className="mt-0.5" /><div><dt className="text-[10px] uppercase tracking-widest text-white/45">Program Status</dt><dd className="text-sm font-semibold" style={{ color: GOLD }}>{statusLabel}</dd></div></div>
                </dl>
              </div>
              {(program.public_media || []).filter((m) => !m.mime?.startsWith('image/')).length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>Program Materials</h3>
                  <div className="space-y-2">{(program.public_media || []).filter((m) => !m.mime?.startsWith('image/')).map((m) => (
                    <a key={m.media_id} href={`${BACKEND}${m.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline" style={{ color: GOLD }} data-testid={`public-doc-${m.media_id}`}><FileText size={14} /> {m.name}</a>
                  ))}</div>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
